import React, { useEffect, useState } from "react";
import { ChevronLeft, Trophy } from "lucide-react";

// Faux public-portal preview shown in the marketing hero. Renders two mini
// screens side-by-side — the season index (competitions list) and one
// competition's results — so visitors get a feel for the read-only public
// page without needing a live share link.
//
// The two "featured" competitions (index 0 and 1) alternate every few
// seconds — the left panel's highlight sweeps down, and the right panel
// cross-fades to the matching results. Respects prefers-reduced-motion.

const COMPS = [
  {
    name: "Open PDI · Round 3",
    date: "12 Feb 2026",
    type: "PDI",
    cat: "Open",
    judge: "David Reeves ARPS",
    rows: [
      { rank: 1, name: "Sarah Bennett", code: "492", title: "Kingfisher at Dawn", score: 20, place: "FIRST" },
      { rank: 2, name: "Charlotte Allen", code: "104", title: "Rooftops in Fog", score: 19, place: "SECOND" },
      { rank: 3, name: "Lily Davies", code: "053", title: "Old Boot", score: 18, place: "THIRD" },
      { rank: 4, name: "Tom Evans", code: "892", title: "Estuary Light", score: 18, place: "HC" },
      { rank: 5, name: "Michael Wilson", code: "264", title: "Steel Yard", score: 17, place: "C" },
    ],
  },
  {
    name: "Nature Print",
    date: "29 Jan 2026",
    type: "Print",
    cat: "Nature",
    judge: "Elaine Marsh FRPS",
    rows: [
      { rank: 1, name: "Peter Clarke", code: "318", title: "Red Deer Rutting", score: 20, place: "FIRST" },
      { rank: 2, name: "Sarah Bennett", code: "492", title: "Fox in Frost", score: 19, place: "SECOND" },
      { rank: 3, name: "Margaret Ellis", code: "617", title: "Barn Owl", score: 18, place: "THIRD" },
      { rank: 4, name: "James Howard", code: "225", title: "Autumn Beech", score: 17, place: "HC" },
      { rank: 5, name: "Lily Davies", code: "053", title: "Mountain Hare", score: 16, place: "C" },
    ],
  },
  // Static rows (not part of the animation cycle)
  { name: "Monochrome Set", date: "15 Jan 2026", type: "PDI", cat: "Mono" },
  { name: "Themed: Reflections", date: "18 Dec 2025", type: "PDI", cat: "Themed" },
];

const CYCLE_MS = 4200;

// Honours the OS-level reduced-motion preference. Users who've opted out
// (accessibility, or people who find looping animations distracting) see a
// fully static mockup instead.
function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(m.matches);
    update();
    // Older Safari uses addListener; modern browsers use addEventListener.
    if (m.addEventListener) m.addEventListener("change", update);
    else m.addListener(update);
    return () => {
      if (m.removeEventListener) m.removeEventListener("change", update);
      else m.removeListener(update);
    };
  }, []);
  return reduced;
}

export default function PortalMockup() {
  const reduced = useReducedMotion();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (reduced) return; // don't schedule swaps if the user has opted out
    const t = setInterval(() => setActive((a) => (a + 1) % 2), CYCLE_MS);
    return () => clearInterval(t);
  }, [reduced]);

  return (
    <div
      className="relative w-full"
      data-testid="landing-portal-mockup"
      aria-label="Preview of the public portal"
    >
      {/* Chip */}
      <div className="absolute -top-3 left-6 z-10 mono uppercase tracking-[0.25em] text-[10px] px-2.5 py-1 bg-amber-400 text-black rounded-sm shadow-lg">
        Public portal — preview
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md p-4 pt-6"
           style={{ background: "linear-gradient(180deg,#0d0d0d 0%, #050505 100%)", border: "1px solid #1f1f1f" }}>
        <div className="hidden md:block">
          <PortalIndexMock active={active} />
        </div>
        <PortalResultsMock active={active} />
      </div>

      <p className="muted-text text-[11px] mt-2 mono uppercase tracking-[0.2em] text-right">
        Illustrative preview — your data, your judges, your season
      </p>
    </div>
  );
}

/* ---------- Left panel: season index ---------- */
function PortalIndexMock({ active }) {
  return (
    <div className="rounded-sm p-3" style={{ background: "#0a0a0a", border: "1px solid #1f1f1f" }}>
      <div className="mono text-[9px] uppercase tracking-[0.28em] text-amber-400 mb-1">Season</div>
      <div className="text-base font-semibold tracking-tight mb-3">2025 – 26</div>

      <div className="ccs-label text-[9px] mb-2">Competitions</div>
      <ul className="space-y-2">
        {COMPS.map((c, i) => {
          // Only the two "featured" rows (0 and 1) participate in the sweep.
          const isFeatured = i < 2;
          const isActive = isFeatured && i === active;
          return (
            <li
              key={c.name}
              className="flex items-center justify-between rounded-sm px-2 py-1.5"
              style={{
                background: isActive ? "rgba(251,191,36,0.10)" : "#111",
                border: isActive
                  ? "1px solid rgba(251,191,36,0.45)"
                  : "1px solid #1a1a1a",
                transition: "background-color 500ms ease, border-color 500ms ease",
              }}
            >
              <div className="min-w-0">
                <div className="text-[11px] font-medium truncate">{c.name}</div>
                <div className="mono text-[9px] muted-text truncate">
                  {c.date} · {c.type} · {c.cat}
                </div>
              </div>
              <span
                className="text-amber-400 text-[10px] shrink-0 ml-2"
                style={{
                  opacity: isActive ? 1 : 0.55,
                  transition: "opacity 500ms ease",
                }}
              >
                View →
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ---------- Right panel: one competition's results (cross-fades) ---------- */
function PortalResultsMock({ active }) {
  // Two absolutely-stacked layers, each fully rendered — we just toggle their
  // opacity. Keeps the reserved space stable so nothing else on the page
  // jumps when the swap happens.
  return (
    <div className="rounded-sm p-3 relative" style={{ background: "#0a0a0a", border: "1px solid #1f1f1f", minHeight: 245 }}>
      {COMPS.slice(0, 2).map((c, i) => (
        <div
          key={c.name}
          aria-hidden={active !== i}
          style={{
            position: i === 0 ? "relative" : "absolute",
            inset: i === 0 ? undefined : "0.75rem",
            opacity: active === i ? 1 : 0,
            transition: "opacity 650ms ease",
            pointerEvents: active === i ? "auto" : "none",
          }}
        >
          <ResultsPanel comp={c} />
        </div>
      ))}
    </div>
  );
}

function ResultsPanel({ comp }) {
  return (
    <>
      <div className="flex items-center gap-1 text-[9px] muted-text mb-1.5">
        <ChevronLeft size={9} /> Back to results
      </div>
      <div className="mono text-[9px] uppercase tracking-[0.28em] text-amber-400 mb-1">Results</div>
      <div className="text-base font-semibold tracking-tight leading-tight">{comp.name}</div>
      <div className="mono text-[9px] muted-text mb-3 mt-0.5">
        {comp.date} · {comp.type} · {comp.cat} · Judge: {comp.judge}
      </div>

      <div className="rounded-sm overflow-hidden" style={{ border: "1px solid #1a1a1a" }}>
        <div className="grid grid-cols-12 gap-1 px-2 py-1 mono text-[8px] uppercase tracking-wider muted-text"
             style={{ background: "#111" }}>
          <span className="col-span-1">#</span>
          <span className="col-span-5">Photographer</span>
          <span className="col-span-3">Title</span>
          <span className="col-span-1 text-right">Sc.</span>
          <span className="col-span-2 text-right pr-1">Place</span>
        </div>
        {comp.rows.map((r) => (
          <div key={r.rank} className="grid grid-cols-12 gap-1 px-2 py-1.5 text-[10px]"
               style={{ borderTop: "1px solid #151515" }}>
            <span className="col-span-1 mono text-amber-400 font-semibold">#{r.rank}</span>
            <span className="col-span-5 truncate">
              {r.name}
              <span className="mono text-[9px] muted-text ml-1">{r.code}</span>
            </span>
            <span className="col-span-3 truncate muted-text">{r.title}</span>
            <span className="col-span-1 text-right mono font-semibold">{r.score}</span>
            <span className="col-span-2 text-right pr-1">
              <PlacementBadge p={r.place} />
            </span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-1 mt-2 text-[9px] muted-text mono">
        <Trophy size={10} className="text-amber-400" /> No sign-in needed — view or print
      </div>
    </>
  );
}

function PlacementBadge({ p }) {
  const styles = {
    FIRST: { bg: "rgba(251,191,36,0.18)", fg: "#fbbf24", label: "1st" },
    SECOND: { bg: "rgba(148,163,184,0.16)", fg: "#cbd5e1", label: "2nd" },
    THIRD: { bg: "rgba(180,83,9,0.18)", fg: "#f59e0b", label: "3rd" },
    HC: { bg: "rgba(16,185,129,0.12)", fg: "#10b981", label: "HC" },
    C: { bg: "rgba(59,130,246,0.12)", fg: "#60a5fa", label: "C" },
  }[p] || { bg: "#111", fg: "#666", label: "—" };
  return (
    <span
      className="inline-block px-1 rounded-sm mono text-[8px] font-semibold"
      style={{ background: styles.bg, color: styles.fg, border: `1px solid ${styles.fg}30` }}
    >
      {styles.label}
    </span>
  );
}
